/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Ember from 'ember';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';

import ApiService from 'cio/services/api';
import NewsletterModel from 'cio/models/newsletter';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import moment from 'moment-timezone';
import { DETAILED_FORMAT_WITH_UTC_OFFSET } from 'cio/utils/date';

export const TOTAL_SENDING_HOURS = 26;
const SEND_STATUS_POLLING_INTERVAL = 5000;

type NewsletterSendInProgressWarningArgs = {
  newsletter: NewsletterModel;
};

export default class NewsletterSendInProgressWarning extends Component<NewsletterSendInProgressWarningArgs> {
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;

  @tracked sendStatus!: { timestamp: number; enqueued_count: number };
  @tracked showStopSendingModal = false;

  get cancellableUntilIsInFuture() {
    let timestamp = this.sendStatus?.timestamp;
    return timestamp ? Math.floor(Date.now() / 1000) < timestamp : false;
  }

  // When a newsletter has rate limiting enabled:
  // Technically, the next "scheduledAt" is the time when the newsletter will
  // send the next batch. If there are too many messages left to be sent, the
  // newsletter will set a new "scheduledAt" to send another batch.
  // In the past the UI would try and estimate itself when the whole send would
  // finish, based on the rate limit send rate, but that wasn't reliable.
  // More details: https://github.com/customerio/ui/pull/5068
  get formattedEstimatedRateLimitedSendFinish() {
    let { scheduledAt, totalSent, originalRecipientCount, rateLimitEmailRate, rateLimitTimePeriod } =
      this.args.newsletter;

    scheduledAt = scheduledAt ?? 0;

    if (scheduledAt !== 0 && originalRecipientCount > 0 && rateLimitEmailRate > 0) {
      let remainingReceipients = originalRecipientCount - totalSent;
      let batchesRemaining = remainingReceipients / rateLimitEmailRate;
      scheduledAt += batchesRemaining * rateLimitTimePeriod - rateLimitTimePeriod;
    }

    return moment.unix(scheduledAt).format(DETAILED_FORMAT_WITH_UTC_OFFSET);
  }

  get finishedSendingInUserTimeZoneAt() {
    return this.args.newsletter.scheduledAt
      ? this.args.newsletter.scheduledAt + TOTAL_SENDING_HOURS * 60 * 60
      : undefined;
  }

  pollForSendStatus = task({ drop: true }, async (newsletterId: string) => {
    while (this.sendStatus == null || this.cancellableUntilIsInFuture) {
      this.sendStatus = await this.api.request(`newsletters/${newsletterId}/send_status`);
      await timeout(SEND_STATUS_POLLING_INTERVAL);
    }
  });

  @action
  openStopSendingModal() {
    this.analytics.track('broadcast', 'sending_cancel_started', {
      model: this.args.newsletter,
    });

    this.showStopSendingModal = true;
  }

  constructor(owner: unknown, args: NewsletterSendInProgressWarningArgs) {
    super(owner, args);

    if (!args.newsletter.rateLimitEnabled && !args.newsletter.tzMatchEnabled && !Ember.testing) {
      this.pollForSendStatus.perform(args.newsletter.id);
    }
  }
}
